/**
 * DOB + age utilities.
 *
 * IMPORTANT (matches legacy behavior):
 * - Age is calculated in **whole years** (i.e. floor), based on whether the birthday has occurred yet.
 *   This yields boundaries like:
 *   - 16 years 0 months 0 days (on the 16th birthday)
 *   - 15 years 11 months 29 days (day before the 16th birthday)
 *   - 105 years 11 months 29 days (day before the 106th birthday)
 *
 * NOTE on leap-day birthdays (Feb 29):
 * This implementation treats the birthday as occurring on Mar 1 in non‑leap years
 * (same outcome as the existing month/day comparison logic used across the codebase).
 */

export type DobInput = string | Date | null | undefined;

function isValidDate(d: Date): boolean {
  return d instanceof Date && Number.isFinite(d.getTime());
}

/**
 * Strict calendar-date guard: verifies that the constructed Date's year/month/day
 * still match the original inputs. This catches JavaScript's silent date overflow
 * (e.g. new Date(2025, 1, 31) → Mar 3 2025) which would otherwise pass isValidDate.
 *
 * @param d         - A Date object that has already passed isValidDate.
 * @param year      - The expected full year (e.g. 2025).
 * @param monthIndex - The expected 0-based month index (0 = January).
 * @param day       - The expected day of the month (1–31).
 */
export function isCalendarDate(d: Date, year: number, monthIndex: number, day: number): boolean {
  return (
    d.getFullYear() === year &&
    d.getMonth() === monthIndex &&
    d.getDate() === day
  );
}

/**
 * Construct a Date object representing local year, month, and day.
 * Avoids JavaScript's native behavior of mapping years 0–99 to 1900–1999.
 */
export function createLocalDate(year: number, monthIndex: number, day: number): Date {
  const d = new Date(0);
  d.setFullYear(year, monthIndex, day);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Parse an input into a Date.
 * - Supports Date objects
 * - Supports ISO date-only strings ("YYYY-MM-DD") as **local** dates (avoids UTC day-shift bugs)
 * - Falls back to Date.parse for full ISO datetime strings
 */
export function parseDobToDate(value: DobInput): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) {
    return isValidDate(value) ? value : undefined;
  }
  if (typeof value !== "string") return undefined;

  const raw = value.trim();
  if (!raw) return undefined;

  // Date-only ISO (common from API): treat as local date to avoid timezone shifting.
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const year = Number(m[1]);
    const monthIndex = Number(m[2]) - 1;
    const day = Number(m[3]);
    const d = createLocalDate(year, monthIndex, day);
    // Guard against JS silent overflow (e.g. Feb 31 → Mar 3): the constructed
    // Date's components must match the original input exactly.
    if (!isValidDate(d) || !isCalendarDate(d, year, monthIndex, day)) return undefined;
    return d;
  }

  const ts = Date.parse(raw);
  if (!Number.isFinite(ts)) return undefined;
  const d = new Date(ts);
  return isValidDate(d) ? d : undefined;
}

/**
 * Validate and sanitise any date-like input for use in form preprocessors.
 *
 * - `Date` objects: checks the Date is finite/valid. Note: JS silent overflow
 *   (e.g. `new Date(2025, 1, 31)` → Mar 3) cannot be detected once the Date has
 *   already been constructed. That guard lives in `DateField.handleChange` via the
 *   MUI `onError` callback, which fires before an overflowed Date reaches RHF.
 * - `string` values: delegated to `parseDobToDate` for strict ISO parsing with
 *   full calendar-overflow detection.
 * - `null` / `undefined`: returned as `undefined`.
 *
 * Use this inside Zod `z.preprocess()` for all date/time fields.
 */
export function sanitiseDateInput(value: unknown): Date | undefined {
  if (value === null || value === undefined) return undefined;
  if (value instanceof Date) {
    return isValidDate(value) ? value : undefined;
  }
  if (typeof value === 'string') return parseDobToDate(value);
  return undefined;
}


function toLocalDateOnly(d: Date): Date {
  return createLocalDate(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Calculate age in **whole years** (floor), or undefined when invalid/future DOB.
 */
export function calculateAge(dob: DobInput, asOf: Date = new Date()): number | undefined {
  const dobDate = parseDobToDate(dob);
  if (!dobDate) return undefined;

  const dobLocal = toLocalDateOnly(dobDate);
  const asOfLocal = toLocalDateOnly(asOf);
  if (!isValidDate(dobLocal) || !isValidDate(asOfLocal)) return undefined;

  // Future DOBs are invalid
  if (dobLocal.getTime() > asOfLocal.getTime()) return undefined;

  let age = asOfLocal.getFullYear() - dobLocal.getFullYear();
  const m = asOfLocal.getMonth() - dobLocal.getMonth();
  if (m < 0 || (m === 0 && asOfLocal.getDate() < dobLocal.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : undefined;
}

export function isAgeAtLeast(dob: DobInput, minAgeYears: number, asOf: Date = new Date()): boolean {
  if (!Number.isFinite(minAgeYears)) return true;
  const age = calculateAge(dob, asOf);
  return typeof age === "number" ? age >= minAgeYears : false;
}

export function isAgeAtMost(dob: DobInput, maxAgeYears: number, asOf: Date = new Date()): boolean {
  if (!Number.isFinite(maxAgeYears)) return true;
  const age = calculateAge(dob, asOf);
  return typeof age === "number" ? age <= maxAgeYears : false;
}

/**
 * Convenience for DOB pickers: derive the allowable DOB range for a (minAge,maxAge) rule.
 *
 * - minDob: earliest DOB allowed (oldest person allowed)
 * - maxDob: latest DOB allowed (youngest person allowed)
 */
export function getDobRangeFromAgeRules(params: {
  minAgeYears?: number;
  maxAgeYears?: number;
  asOf?: Date;
}): { minDob?: Date; maxDob?: Date } {
  const asOf = toLocalDateOnly(params.asOf ?? new Date());
  const out: { minDob?: Date; maxDob?: Date } = {};

  // Latest DOB allowed for minAge: asOf - minAge years (inclusive).
  if (typeof params.minAgeYears === "number" && Number.isFinite(params.minAgeYears)) {
    out.maxDob = new Date(
      asOf.getFullYear() - params.minAgeYears,
      asOf.getMonth(),
      asOf.getDate()
    );
  }

  // Earliest DOB allowed for maxAge (whole-years floor):
  // Must be younger than (maxAge+1) birthday → DOB >= (asOf - (maxAge+1) years + 1 day)
  if (typeof params.maxAgeYears === "number" && Number.isFinite(params.maxAgeYears)) {
    const base = new Date(
      asOf.getFullYear() - (params.maxAgeYears + 1),
      asOf.getMonth(),
      asOf.getDate()
    );
    const minDob = new Date(base);
    minDob.setDate(minDob.getDate() + 1);
    out.minDob = minDob;
  }

  return out;
}